<template>
  <div class="row" :class="isUser ? 'user' : 'assistant'">
    <Avatar v-if="!isUser" kind="agent" :size="28" />
    <div class="wrap">
      <div class="meta">
        <span class="author">{{ isUser ? userName : "Ecosa PM" }}</span>
        <span v-if="timeLabel" class="time">{{ timeLabel }}</span>
      </div>
      <div class="bubble" :class="isUser ? 'user' : 'assistant'">
        <div v-if="isUser" class="text">{{ msg.content }}</div>
        <div v-else class="text md" v-html="renderedHtml" />
      </div>
      <div v-if="images.length" class="images">
        <img v-for="(src, i) in images" :key="i" :src="src" alt="attachment" />
      </div>
      <div v-if="docs.length" class="docs">
        <a
          v-for="(d, i) in docs"
          :key="i"
          class="doc-chip"
          :href="d.url || '#'"
          :target="d.url ? '_blank' : undefined"
          rel="noopener"
          :title="d.name"
        >
          <span class="doc-badge">{{ d.badge }}</span>
          <span class="doc-name">{{ d.name }}</span>
        </a>
      </div>
    </div>
    <Avatar v-if="isUser" kind="user" :initials="userInitials" :size="28" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { marked } from "marked";
import DOMPurify from "dompurify";
import Avatar from "@/components/design/Avatar.vue";
import { useAuthStore } from "@/stores/auth";
import { displayNameFor, initialsFor } from "@/lib/user";
import { formatTime } from "@/lib/time";
import type { MessageOut } from "@/types/api";

const props = defineProps<{ msg: MessageOut }>();
const auth = useAuthStore();

const isUser = computed(() => props.msg.role === "user");
const userName = computed(() => displayNameFor(auth.me, "You"));
const userInitials = computed(() => initialsFor(userName.value));
const timeLabel = computed(() => formatTime(props.msg.create_time));

const renderedHtml = computed(() =>
  DOMPurify.sanitize(marked.parse(props.msg.content ?? "", { breaks: true }) as string)
);

const images = computed<string[]>(() => {
  const list = props.msg.attachments;
  if (!Array.isArray(list)) return [];
  return list
    .filter((a: any) => typeof a?.mime === "string" && a.mime.startsWith("image/"))
    .map((a: any) => a?.url ?? a?.s3_key)
    .filter(Boolean);
});

function badgeFor(mime: string, name: string): string {
  const m = (mime || "").toLowerCase();
  const n = (name || "").toLowerCase();
  if (m === "application/pdf" || n.endsWith(".pdf")) return "PDF";
  if (m === "application/json" || n.endsWith(".json")) return "{ }";
  if (m === "text/csv" || n.endsWith(".csv")) return "CSV";
  if (m === "text/markdown" || n.endsWith(".md")) return "MD";
  if (m === "text/html" || /\.html?$/.test(n)) return "HTM";
  if (m.includes("xml") || n.endsWith(".xml")) return "XML";
  return "TXT";
}

const docs = computed<{ name: string; badge: string; url: string | null }[]>(() => {
  const list = props.msg.attachments;
  if (!Array.isArray(list)) return [];
  return list
    .filter((a: any) => typeof a?.mime === "string" && !a.mime.startsWith("image/"))
    .map((a: any) => ({
      name: a?.filename || a?.s3_key || "attachment",
      badge: badgeFor(a?.mime ?? "", a?.filename ?? ""),
      url: (a?.url as string | null) ?? null,
    }));
});
</script>

<style scoped>
.row {
  display: flex;
  gap: 10px;
  margin: 14px 0;
  align-items: flex-start;
}
.row.user {
  flex-direction: row-reverse;
}
.wrap {
  max-width: 640px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.row.user .wrap {
  align-items: flex-end;
}
.meta {
  display: flex;
  gap: 8px;
  align-items: baseline;
  margin-bottom: 4px;
  font-size: 11.5px;
}
.author {
  color: var(--ink-2);
  font-weight: 500;
}
.time {
  color: var(--ink-5);
  font-size: 10.5px;
}
.bubble {
  padding: 11px 14px;
  border-radius: 12px;
  line-height: 1.6;
  font-size: 14px;
  color: var(--ink);
  word-wrap: break-word;
  overflow-wrap: break-word;
}
.bubble.assistant {
  background: var(--surface);
  border: 1px solid var(--border);
  border-top-left-radius: 4px;
}
.bubble.user {
  background: var(--ink);
  color: #fff;
  border-top-right-radius: 4px;
}
.text {
  white-space: pre-wrap;
}
.text.md {
  white-space: normal;
}
.text.md :deep(p) {
  margin: 6px 0;
  line-height: 1.65;
}
.text.md :deep(p:first-child) {
  margin-top: 0;
}
.text.md :deep(p:last-child) {
  margin-bottom: 0;
}
.text.md :deep(ul),
.text.md :deep(ol) {
  margin: 8px 0;
  padding-left: 22px;
}
.text.md :deep(li) {
  margin: 4px 0;
  line-height: 1.6;
}
.text.md :deep(h1),
.text.md :deep(h2) {
  font-size: 14px;
  font-weight: 600;
  margin: 14px 0 6px;
  color: var(--ink);
}
.text.md :deep(h3) {
  font-size: 13.5px;
  font-weight: 600;
  margin: 12px 0 4px;
}
.text.md :deep(code) {
  background: var(--hover);
  padding: 0 4px;
  border-radius: 3px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
}
.text.md :deep(pre) {
  background: #0b0f14;
  color: #d6d9dd;
  padding: 10px 12px;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 12px;
}
.text.md :deep(strong) {
  color: var(--ink);
  font-weight: 600;
}
.images {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.row.user .images {
  justify-content: flex-end;
}
.images img {
  max-width: 220px;
  max-height: 220px;
  border-radius: 8px;
  border: 1px solid var(--border);
}
.docs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 8px;
}
.row.user .docs {
  justify-content: flex-end;
}
.doc-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font-size: 12px;
  text-decoration: none;
  max-width: 260px;
}
.doc-chip:hover {
  background: var(--hover);
}
.doc-badge {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #475569;
  background: #eef2f7;
  padding: 2px 6px;
  border-radius: 4px;
  flex: 0 0 auto;
}
.doc-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
